import { clsx } from 'clsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import React, { useEffect, useRef, useState } from 'react';
import { ExpandCollapseIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/UseExistingKBAccordion/ExpandCollapseIcon/ExpandCollapseIcon';
import { CreateNewKBInput } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CreateNewKBAccordion/CreanteNewKBInput';
import {
  useAssignProjectKnowledgeBaseMutation,
  useCreateKnowledgeBaseMutation,
  useGetKnowledgeBaseByIdQuery,
} from 'src/services';
import { useDispatch } from 'react-redux';
import { Skeleton } from 'src/components/ui/skeleton';
import { ProductsTypeBack } from 'src/constants/products';
import { CreateNewKBDataSources } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CreateNewKBAccordion/CreanteNewKBDataSources';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { AddItemIconDisabled } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIconDisabled';
import { setKnowledgeBase } from 'src/store/slices/onboardingSlice';

const LoadingSkeleton = () => {
  return (
    <div className={'flex flex-col gap-5 min-w-[455px]'}>
      <Skeleton className={'w-full h-4'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-4'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
    </div>
  );
};

export const CreateNewKBAccordion = ({
  project,
  item,
  knowledgeBases = [],
  onCollectorsChanged = (_collectors?: any) => {},
  onDisabledChange = (_disabled?: any) => {},
}: {
  project?: any;
  item?: any;
  knowledgeBases?: any[];
  onCollectorsChanged?: (collectors: any) => void;
  onDisabledChange?: (disabled: any) => void;
}) => {
  const isInit = useRef(null);
  const [value, setValue] = useState('');
  const [currentKnowledgeBase, setCurrentKnowledgeBase] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [isKBCreated, setIsKBCreated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useDispatch();
  const [createKnowledgeBase, { data, isLoading, isSuccess }] =
    useCreateKnowledgeBaseMutation() as any;
  const { data: currentKnowledgeBaseData, isLoading: isLoadingCurrentKnowledgeBaseData } =
    useGetKnowledgeBaseByIdQuery(
      {
        id: currentKnowledgeBase?.id,
        load_projects: true,
      },
      { skip: !currentKnowledgeBase?.id },
    );
  const [assignKB] = useAssignProjectKnowledgeBaseMutation();

  useEffect(() => {
    isInit.current = true;

    if (!knowledgeBases.length) {
      setValue('new-kb');
    }
  }, []);

  useEffect(() => {
    isInit.current && setIsExpanded(value === 'new-kb');
  }, [value, isInit.current]);

  useEffect(() => {
    if (item?.id) {
      setCurrentKnowledgeBase(item);
    } else {
      if (isExpanded && !isKBCreated) {
        createKnowledgeBase({ name: `Knowledge Base for ${project?.name || ''}` });
      }
    }
  }, [item, isExpanded, isKBCreated]);

  useEffect(() => {
    if (currentKnowledgeBaseData) {
      dispatch(setKnowledgeBase(currentKnowledgeBaseData));
      setCurrentKnowledgeBase(currentKnowledgeBaseData);
    }
  }, [currentKnowledgeBaseData]);

  useEffect(() => {
    if (isSuccess && data) {
      setIsKBCreated(true);
      setCurrentKnowledgeBase(data);

      assignKB({
        project_type: ProductsTypeBack[project?.project_type],
        projectId: project.id,
        body: [data.id],
      });

      dispatch(setKnowledgeBase(data));
      setValue('new-kb');
    }
  }, [isSuccess, data]);

  return (
    <div
      className={clsx(
        'flex flex-row items-center justify-center gap-5',
        !isExpanded && 'w-[455px]',
      )}
    >
      <Accordion
        value={value}
        defaultValue={isExpanded ? 'new-kb' : ''}
        onValueChange={setValue}
        type="single"
        className="w-full"
        collapsible={true}
        disabled={disabled}
      >
        <AccordionItem
          value="new-kb"
          className={clsx(
            'group/main-accordion-item rounded-sm-5 border-0 transition-shadow shadow-none hover:!shadow-none',
            !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
            disabled && 'opacity-70',
          )}
        >
          <AccordionTrigger
            hideDefaultIcon
            className="py-2.5 px-0 group/trigger w-full flex flex-row items-start gap-5"
          >
            <div className={clsx((isLoading || !currentKnowledgeBase || !isExpanded) && 'hidden')}>
              <CheckIcon className={'invisible w-[27px]'} />
            </div>
            <div className="flex items-center justify-between w-full">
              <div
                className={
                  'flex flex-row gap-[7px] text-violet font-semibold text-sm group-hover/trigger:underline'
                }
              >
                <p>Create new knowledge base</p>
              </div>
              <ExpandCollapseIcon expanded={isExpanded} />
            </div>
            <div
              className={clsx(
                'invisible w-[27px]',
                (isLoading || !currentKnowledgeBase || !isExpanded) && 'hidden',
              )}
            >
              <AddItemIconDisabled />
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={clsx(
              'px-0 pb-0 mt-4',
              (!currentKnowledgeBase || !isExpanded) && 'opacity-0',
            )}
          >
            {!currentKnowledgeBase || isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className={'flex flex-col gap-1.5'}>
                <CreateNewKBInput item={currentKnowledgeBase} />
                {currentKnowledgeBase && (
                  <CreateNewKBDataSources
                    item={currentKnowledgeBase}
                    onCollectorsChanged={onCollectorsChanged}
                    onDisabledChange={onDisabledChange}
                  />
                )}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
